import React, { useCallback, useContext, useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import Box from '@material-ui/core/Box';
import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { PersonContext } from '../../contexts/PersonContext';
import { create } from '../../feathersWrapper';
import { axisLabels, monthAxisLabels, tooltipFormatter } from '../../functions/ChartFunctions';
import { sumProperty } from '../../functions/SumFunctions';

export default function CashflowGraph({ stale, setStale, view, id, filter, tabValue }) {
  const { organizationId, basis } = useContext(PersonContext);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [barGraphData, setBarGraphData] = useState(null);

  const [name, setName] = useState(null);
  const [dataKey, setDataKey] = useState(null);
  const [sumValue, setSumValue] = useState(null);

  const getData = useCallback(async () => {
    const query = { organizationId, basis };

    if (view === 'property') {
      query.propertyId = id;
    } else if (view === 'unit') {
      query.unitId = id;
    } else if (view === 'subportfolio') {
      query.entityId = id;
    }

    const reportData = await create(
      this,
      'reports',
      {
        reportName: 'cashflowCard',
        startDate: filter.startDate,
        endDate: filter.endDate,
        ...query,
      },
      true,
    );
    setBarGraphData(reportData);

    let name;
    let dataKey;
    let sumValue;
    if (tabValue === 'Cashflow') {
      name = 'Operating Cashflow';
      dataKey = 'cash';
      sumValue = sumProperty(reportData, 'cash');
    } else if (tabValue === 'NOI') {
      name = 'Net Operating Income';
      dataKey = 'income';
      sumValue = sumProperty(reportData, 'income');
    } else {
      name = 'Revenue';
      dataKey = 'revenue';
      sumValue = sumProperty(reportData, 'revenue');
    }

    setName(name);
    setDataKey(dataKey);
    setSumValue(sumValue);
  }, [organizationId, basis, view, id, filter.startDate, filter.endDate, tabValue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (loading) {
          await getData();
          setLoading(false);
        }
        if (stale) {
          await getData();
          setStale(false);
        }
      } catch {
        setBarGraphData(null);
        setLoading(false);
        if (stale) {
          setStale(false);
        }
      }
    };
    fetchData();
  }, [getData, loading, setStale, stale]);

  if (loading || !Array.isArray(barGraphData)) {
    return <Box display="flex" flexDirection="column" height={1} px={2} pt={2} pb={3} minHeight="346px" />;
  }

  return (
    <>
      <Typography variant="h4">
        <NumberFormat
          displayType="text"
          value={sumValue}
          thousandSeparator
          prefix="$"
          decimalScale={0}
          fixedDecimalScale
        />
        <Box color={theme.palette.action.active} marginLeft={1} component="span">
          <Typography variant="overline" component="span">
            {filter.filterText}
          </Typography>
        </Box>
      </Typography>
      <Box pb={2}>
        <ResponsiveContainer width="100%" height={275}>
          <ComposedChart
            data={barGraphData}
            margin={{
              top: 30,
              right: 0,
              bottom: 0,
              left: 0,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis dataKey="name" tickFormatter={monthAxisLabels} />
            <YAxis axisLine={false} tickLine={false} tickFormatter={axisLabels} domain={['auto', 1000]} />
            <Tooltip formatter={tooltipFormatter} />
            <Bar name={name} dataKey={dataKey} stackId="x" barSize={50} isAnimationActive={false}>
              {barGraphData.map((entry) => {
                let color;
                if (tabValue === 'Cashflow') {
                  color = entry.cash < 0 ? '#4F7CAC' : '#82CA9D';
                } else if (tabValue === 'NOI') {
                  color = entry.income < 0 ? '#4F7CAC' : '#82CA9D';
                } else {
                  color = entry.revenue < 0 ? '#4F7CAC' : '#82CA9D';
                }
                return <Cell key={`cell-${entry.name}`} fill={color} />;
              })}
            </Bar>
            <ReferenceLine y={0} stroke="#000" />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
}

CashflowGraph.defaultProps = {
  view: '',
  id: null,
};
CashflowGraph.propTypes = {
  stale: PropTypes.bool.isRequired,
  setStale: PropTypes.func.isRequired,
  view: PropTypes.string,
  id: PropTypes.number,
  filter: PropTypes.objectOf(PropTypes.string).isRequired,
  tabValue: PropTypes.string.isRequired,
};
